import { Clock, Inbox, Timer, TrendingUp } from "lucide-react";
import { rotuloSeveridade } from "./severidade";
import { textoDeDias, type ResumoAba, type ResumoResolvidos } from "./resumo";

const CORES_SEVERIDADE = [
  "bg-red-500",
  "bg-orange-500",
  "bg-yellow-400",
  "bg-green-500",
];

interface ResumoChamadosProps {
  resumo: ResumoAba;
  resolvidos?: ResumoResolvidos;
  carregandoResolvidos: boolean;
  /** Quem lê é da equipe T4i: o que espera ação está parado com o cliente. */
  equipe?: boolean;
}

/**
 * Quatro indicadores e a composição por severidade.
 *
 * "Aguardando você" é o único que pede ação de quem lê, então vem primeiro. Os
 * resolvidos saem de consulta própria, sem período: é o que foi entregue nos
 * últimos 90 dias, independente da aba aberta.
 */
export const ResumoChamados = ({
  resumo,
  resolvidos,
  carregandoResolvidos,
  equipe = false,
}: ResumoChamadosProps) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <Indicador
      icone={<Inbox className="w-4 h-4" />}
      rotulo={equipe ? "Aguardando o cliente" : "Aguardando você"}
      valor={resumo.aguardandoVoce}
      destaque={resumo.aguardandoVoce > 0}
      detalhe={
        resumo.esperaMaisLongaDias !== null
          ? `a mais antiga, ${textoDeDias(resumo.esperaMaisLongaDias)}`
          : equipe
            ? "nada parado com o cliente"
            : "nada parado com você"
      }
    />

    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-5">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
        <TrendingUp className="w-4 h-4" />
        Nesta aba
      </p>
      <p className="text-3xl font-bold text-gray-900 mt-1">{resumo.total}</p>
      <ComposicaoPorSeveridade porSeveridade={resumo.porSeveridade} total={resumo.total} />
    </div>

    <Indicador
      icone={<Clock className="w-4 h-4" />}
      rotulo="Resolvidos em 90 dias"
      valor={carregandoResolvidos ? "…" : resolvidos?.resolvidos ?? 0}
      detalhe="independente da aba aberta"
    />

    <Indicador
      icone={<Timer className="w-4 h-4" />}
      rotulo="Tempo de resolução"
      valor={
        carregandoResolvidos
          ? "…"
          : resolvidos?.medianaDias === null || resolvidos === undefined
            ? "—"
            : `${resolvidos.medianaDias} d`
      }
      detalhe="mediana, da abertura ao fechamento"
    />
  </div>
);

const Indicador = ({
  icone,
  rotulo,
  valor,
  detalhe,
  destaque,
}: {
  icone: React.ReactNode;
  rotulo: string;
  valor: number | string;
  detalhe: string;
  destaque?: boolean;
}) => (
  <div
    className={[
      "rounded-2xl border p-5 backdrop-blur-sm",
      destaque
        ? "bg-blue-50/80 border-blue-200"
        : "bg-white/70 border-gray-200/60",
    ].join(" ")}
  >
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
      {icone}
      {rotulo}
    </p>
    <p
      className={[
        "text-3xl font-bold mt-1",
        destaque ? "text-blue-800" : "text-gray-900",
      ].join(" ")}
    >
      {valor}
    </p>
    <p className="text-xs text-gray-500 mt-1">{detalhe}</p>
  </div>
);

const ComposicaoPorSeveridade = ({
  porSeveridade,
  total,
}: {
  porSeveridade: [number, number, number, number];
  total: number;
}) => {
  if (total === 0) {
    return <p className="text-xs text-gray-500 mt-3">nenhum chamado no recorte</p>;
  }

  return (
    <div className="mt-3">
      <div
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="img"
        aria-label={porSeveridade
          .map((q, i) => `${q} ${rotuloSeveridade(i)}`)
          .filter((_, i) => porSeveridade[i] > 0)
          .join(", ")}
      >
        {porSeveridade.map((quantidade, indice) =>
          quantidade === 0 ? null : (
            <span
              key={indice}
              className={CORES_SEVERIDADE[indice]}
              style={{ width: `${(quantidade / total) * 100}%` }}
              title={`${quantidade} ${rotuloSeveridade(indice)}`}
            />
          )
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {porSeveridade.map((quantidade, indice) =>
          quantidade === 0 ? null : (
            <span key={indice} className="text-[11px] text-gray-600 inline-flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${CORES_SEVERIDADE[indice]}`} />
              {quantidade} {rotuloSeveridade(indice)}
            </span>
          )
        )}
      </div>
    </div>
  );
};
